import React from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { PromptLoginWidgetProps, PromptLoginWidgetState } from "./types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { themeColor } from "../../../../types/color";

export const defaultPromptLoginState: PromptLoginWidgetState = {
  // Container styling
  containerBgColor: themeColor("secondary"),
  containerBorderRadius: 16,
  containerPaddingX: 24,
  containerPaddingY: 24,
  containerBorderWidth: 1,
  containerBorderColor: themeColor("background"),

  // Icon styling
  iconBgColor: themeColor("primary"),
  iconColor: themeColor("secondary"),
  iconSize: 40,

  // Title styling
  titleColor: themeColor("textPrimary"),
  titleFontSize: 16,
  titleFontWeight: "700",

  // Description styling
  descriptionColor: themeColor("textPrimary"),
  descriptionFontSize: 14,

  // Button styling
  buttonBgColor: themeColor("primary"),
  buttonTextColor: themeColor("buttonText"),
  buttonFontSize: 14,
  buttonFontWeight: "600",
  buttonBorderRadius: 10,
  buttonPaddingX: 20,
  buttonPaddingY: 10,
};

const editFields: EditField[] = [
  { key: "containerBgColor", label: "Container Background", type: "color" },
  {
    key: "containerBorderRadius",
    label: "Container Border Radius",
    type: "number",
    min: 0,
    max: 24,
  },
  {
    key: "containerBorderWidth",
    label: "Container Border Width",
    type: "number",
    min: 0,
    max: 4,
  },
  { key: "containerBorderColor", label: "Container Border Color", type: "color" },
  { key: "iconBgColor", label: "Icon Background", type: "color" },
  { key: "iconColor", label: "Icon Color", type: "color" },
  { key: "titleColor", label: "Title Color", type: "color" },
  {
    key: "titleFontSize",
    label: "Title Font Size",
    type: "number",
    min: 12,
    max: 24,
  },
  { key: "descriptionColor", label: "Description Color", type: "color" },
  {
    key: "descriptionFontSize",
    label: "Description Font Size",
    type: "number",
    min: 10,
    max: 20,
  },
  { key: "buttonBgColor", label: "Button Background", type: "color" },
  { key: "buttonTextColor", label: "Button Text Color", type: "color" },
  {
    key: "buttonBorderRadius",
    label: "Button Border Radius",
    type: "number",
    min: 0,
    max: 24,
  },
];

/** Lock icon SVG rendered inline */
const LockIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg
    width={size * 0.5}
    height={size * 0.5}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

export const PromptLoginWidget: React.FC<PromptLoginWidgetProps> = ({
  widgetId,
  title,
  description,
  buttonText,
  onSignIn,
  disabled = false,
  initialState,
}) => {
  const {
    state: styles,
    handleEditChange,
    handleSave,
    isSaving,
    getEditValues,
    resolveColor,
    themeColors,
    themeFonts,
    themeIcons,
  } = useWidgetEditable<PromptLoginWidgetState>(
    widgetId,
    { ...defaultPromptLoginState, ...initialState },
    editFields,
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName="Prompt Login"
      editFields={editFields}
      editValues={getEditValues()}
      onEditChange={handleEditChange}
      onSave={handleSave}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
      themeIcons={themeIcons}
    >
      <div
        style={{
          backgroundColor: resolveColor(styles.containerBgColor),
          borderRadius: `${styles.containerBorderRadius}px`,
          padding: `${styles.containerPaddingY}px ${styles.containerPaddingX}px`,
          border: `${styles.containerBorderWidth}px solid ${resolveColor(styles.containerBorderColor)}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
          width: "100%",
          boxSizing: "border-box",
          textAlign: "center",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
        }}
      >
        {/* Icon circle */}
        <div
          style={{
            width: `${styles.iconSize}px`,
            height: `${styles.iconSize}px`,
            borderRadius: "50%",
            backgroundColor: resolveColor(styles.iconBgColor),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <LockIcon
            size={styles.iconSize}
            color={resolveColor(styles.iconColor)}
          />
        </div>

        {/* Text content */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <div
            style={{
              color: resolveColor(styles.titleColor),
              fontSize: `${styles.titleFontSize}px`,
              fontWeight: styles.titleFontWeight,
              lineHeight: 1.4,
            }}
          >
            {title}
          </div>
          <div
            style={{
              color: resolveColor(styles.descriptionColor),
              fontSize: `${styles.descriptionFontSize}px`,
              lineHeight: 1.5,
            }}
          >
            {description}
          </div>
        </div>

        {/* Sign In button */}
        {onSignIn && (
          <button
            onClick={disabled ? undefined : onSignIn}
            style={{
              backgroundColor: resolveColor(styles.buttonBgColor),
              color: resolveColor(styles.buttonTextColor),
              fontSize: `${styles.buttonFontSize}px`,
              fontWeight: styles.buttonFontWeight,
              borderRadius: `${styles.buttonBorderRadius}px`,
              padding: `${styles.buttonPaddingY}px ${styles.buttonPaddingX}px`,
              border: "none",
              cursor: disabled ? "default" : "pointer",
              transition: "opacity 0.15s ease",
              opacity: disabled ? 0.5 : 1,
              width: "100%",
              maxWidth: "200px",
              letterSpacing: "0.01em",
              pointerEvents: disabled ? "none" : "auto",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseEnter={(e) => {
              if (!disabled) (e.currentTarget as HTMLButtonElement).style.opacity = "0.88";
            }}
            onMouseLeave={(e) => {
              if (!disabled) (e.currentTarget as HTMLButtonElement).style.opacity = "1";
            }}
          >
            {disabled ? "Loading ..." : buttonText}
          </button>
        )}
      </div>
    </WidgetWrapper>
  );
};
